import React, { useContext } from 'react';
import type { TrackingDataV2 } from '@oneaudi/audi-tracking-service';
import { checkLinkTarget, completeTargetURL, getTrackingSearchParam } from '../utils/trackingUtils';
import { useFeatureAppEnv } from '../context/FeatureAppEnvContext';
import type { DisplayedVehicleType } from '../utils/filterUtils';
import {
  filterCarlineGroupsByVehicleType,
  filterByCarlineGroup,
  filterByBodyType,
  flattenCarlineGroups,
} from '../utils/filterUtils';
import { DataContext } from '../context/DataContext';

export interface CtaTexts {
  primary: string;
  secondary: string;
}
export interface Filter {
  id: string;
  values?: string[];
}

type FilterKind = 'vehicleType' | 'carlineGroup' | 'bodyType';

export type NextState = {
  vehicleTypes: DisplayedVehicleType[];
  carlineGroups: string[];
  bodyTypes: string[];
};

export interface TrackingManager {
  ready: (results: number, variant: 'model' | 'group') => void;
  click: (
    targetURL: string,
    label: string,
    linkValue: string,
    carlineGroup: string,
    carline: string,
    amount?: number,
    elementName?: string,
  ) => void;
  trackFilterClick: (
    label: string,
    value: string,
    numberOfCars: number,
    clickedFilter: Filter[],
    relatedFilters: Filter[],
    isAddingFilter: boolean,
    elementName: string,
    variant: 'group' | 'model',
  ) => void;
  trackResetFilter: (label: string, relatedFilters: Filter[]) => void;
  trackShowMoreFilterClick: (label: string) => void;
  trackShowActionsMenuClick: (value: string, label: string) => void;
  clickVtpLink: (label: string, linktext: string, url: string) => void;
  clickCardImage: (group: string, carline: string, url?: string) => void;
  trackFilterToggle: (args: {
    label: string;
    kind: FilterKind;
    id: string;
    next: NextState;
    added: boolean;
    ui?: 'tag' | 'text link' | 'button' | 'image';
    variant: 'group' | 'model';
  }) => void;
}

export interface TrackingEvent {
  event: {
    eventInfo: {
      eventAction: string;
      eventName: string;
    };
    attributes: Record<string, unknown>;
  };
  componentUpdate: {
    componentInfo: {
      implementer: number;
      version: string;
    };
  };
}

const createTrackingPayload = (
  action: string,
  name: string,
  attributes: Record<string, unknown>,
): TrackingDataV2 => {
  const defaultAttributes = {
    componentName: 'model-selector',
  };

  return {
    event: {
      eventInfo: {
        eventAction: action,
        eventName: name,
      },
      attributes: {
        ...defaultAttributes,
        ...attributes,
      },
    },
  };
};

export const useTrackingManager = (): TrackingManager => {
  const { 's2:logger': logger, 'audi-tracking-service': trackingService } =
    useFeatureAppEnv().featureServices;
  const { allCarlineGroups } = useContext(DataContext);

  const buildRelatedFiltersPayload = (next: NextState) => [
    ...(next.vehicleTypes.length ? [{ id: 'vehicleType', values: next.vehicleTypes }] : []),
    ...(next.carlineGroups.length ? [{ id: 'carlineGroup', values: next.carlineGroups }] : []),
    ...(next.bodyTypes.length ? [{ id: 'bodyTypes', values: next.bodyTypes }] : []),
  ];

  const computeNextCount = React.useCallback(
    (next: NextState): number => {
      const vehicleGroups = filterCarlineGroupsByVehicleType(allCarlineGroups, next.vehicleTypes);
      const vehicleCarlines = flattenCarlineGroups(vehicleGroups);
      const groupCarlines = next.carlineGroups.length
        ? filterByCarlineGroup(vehicleCarlines, next.carlineGroups)
        : vehicleCarlines;
      const bodyCarlines = next.bodyTypes.length
        ? filterByBodyType(groupCarlines, next.bodyTypes)
        : groupCarlines;
      return bodyCarlines.length;
    },
    [allCarlineGroups],
  );

  return React.useMemo(() => {
    const trackFilterToggle = ({
      label,
      kind,
      id,
      next,
      added,
      ui = 'tag',
      variant,
    }: {
      label: string;
      kind: FilterKind;
      id: string;
      next: NextState;
      added: boolean;
      ui?: 'tag' | 'text link' | 'button' | 'image';
      variant: 'group' | 'model';
    }) => {
      const numberOfCars = computeNextCount(next);
      const relatedFilters = buildRelatedFiltersPayload(next);
      trackFilterClick(
        label,
        id,
        numberOfCars,
        [{ id: kind, values: [id] }],
        relatedFilters,
        added,
        ui,
        variant,
      );
    };

    const track = (...args: Parameters<typeof createTrackingPayload>): void => {
      if (trackingService) {
        const payload = createTrackingPayload(...args);
        logger.debug('Sending tracking event: ', JSON.stringify(payload, null, 2));
        trackingService.track(payload);
      }
    };

    const trackShowMoreFilterClick = (label: string): void => {
      track('navigation', 'model selector - click to show more filters', {
        label: '...',
        targetURL: '',
        clickID: '',
        elementName: 'tag',
        value: label,
        pos: '',
      });
    };

    const trackShowActionsMenuClick = (label: string, value: string): void => {
      track('navigation', 'model selector - click to open actions menu', {
        label: label.replace(/\s*\(\d+\)/, ''),
        targetURL: '',
        clickID: '',
        elementName: 'text link',
        value,
        pos: '',
      });
    };

    const trackFilterClick = (
      label: string,
      value: string,
      numberOfCars: number,
      clickedFilter: Filter[],
      relatedFilters: Filter[],
      isAddingFilter: boolean,
      elementName: string,
      variant: 'group' | 'model',
    ): void => {
      const eventAttributes = {
        componentName: 'model-selector',
        label,
        targetURL: 'model selector results',
        clickID: '',
        elementName,
        value,
        pos: numberOfCars.toString(),
        relatedFilters: clickedFilter,
      };

      const componentUpdate = {
        componentInfo: {
          implementer: 2,
          version: __FEATURE_APP_VERSION__,
        },
        attributes: {
          variant: `${variant} tile view`,
          search: {
            name: 'model selector',
            term: '',
            filter: relatedFilters,
            results: numberOfCars.toString(),
          },
        },
      };

      const trackingPayload = {
        event: {
          eventInfo: {
            eventAction: 'filter',
            eventName: `model selector - ${isAddingFilter ? 'add' : 'remove'} filter`,
          },
          attributes: eventAttributes,
        },
        datalayerAdditions: componentUpdate, // Properly separate component update
      };
      logger.debug('Sending tracking event: ', JSON.stringify(trackingPayload, null, 2));

      if (trackingService) {
        trackingService.track(trackingPayload);
      }
    };
    const trackResetFilter = (label: string, relatedFilter: Filter[]) => {
      const deletedFilters = relatedFilter.filter(({ id, values }) => {
        if (id === 'vehicleType' && values?.includes('ALL')) {
          return false;
        }
        return values && values.length > 0;
      });
      track('filter', 'model selector - remove all filters', {
        label,
        targetURL: 'model selector results',
        clickID: '',
        elementName: 'text link',
        value: `reset filter`,
        pos: '',
        relatedFilters: deletedFilters,
      });
    };

    const ready = (results: number, variant: 'group' | 'model') => {
      const filter = getTrackingSearchParam();
      const trackingPayload = {
        componentUpdate: {
          componentInfo: {
            componentID: 'model selector',
            isPrimaryConsumer: true,
          },
          attributes: {
            implementer: 2,
            version: __FEATURE_APP_VERSION__,
            variant: `${variant} tile view`,
            search: {
              name: 'model selector',
              term: '',
              results,
              filter,
            },
          },
        },
        event: {
          eventInfo: {
            eventAction: 'feature_app_ready',
            eventName: 'model selector - page',
          },
          attributes: {
            componentName: 'model-selector',
            label: '',
            targetURL: '',
            clickID: '',
            elementName: '',
            value: '',
            pos: '',
          },
        },
      };
      if (trackingService) {
        logger.debug('Sending tracking event: ', JSON.stringify(trackingPayload, null, 2));
        trackingService.track(trackingPayload);
      }
    };

    const click = (
      targetURL: string,
      label: string,
      linkValue: string,
      carlineGroup: string,
      carline: string,
      amount?: number,
      elementName?: string,
    ) => {
      track(checkLinkTarget(targetURL), `model selector - click on ${linkValue}`, {
        label: label.replace(/\s*\(\d+\)/, ''),
        targetURL: completeTargetURL(targetURL),
        clickID: '',
        elementName: elementName ?? 'text link',
        value: `${carlineGroup}~${carline}`,
        pos: amount ? amount.toString() : '',
      });
    };
    const clickVtpLink = (label: string, linkText: string, url: string) => {
      track(checkLinkTarget(url), label, {
        label: linkText,
        targetURL: window.location.origin + url,
        elementName: 'text link',
        clickID: '',
        pos: '',
        value: '',
      });
    };
    const clickCardImage = (group: string, carline: string, url?: string) => {
      const safeUrl = url ?? '';
      track(checkLinkTarget(safeUrl), 'model selector - click on image', {
        label: '',
        targetURL: window.location.origin + url,
        clickID: '',
        elementName: 'image',
        value: `${group}~${carline}`,
        pos: '',
      });
    };

    return {
      ready,
      click,
      trackShowMoreFilterClick,
      trackShowActionsMenuClick,
      trackFilterToggle,
      trackFilterClick,
      trackResetFilter,
      clickVtpLink,
      clickCardImage,
    };
  }, [trackingService, computeNextCount]);
};
